import type { Timesheet, TimesheetEntry, Employee } from '@/types/timesheet';
import type { Anomaly, AnomalyDetectionResult, EmployeeHistoricalData, AnomalySeverity } from '@/types/anomaly';
import { 
  calculateEntryVariance, 
  calculateTimesheetVarianceSummary,
  formatVariance 
} from './varianceCalculationService';

// Configuration thresholds
const THRESHOLDS = {
  DAILY_HOURS_LIMIT: 12,
  WEEKLY_HOURS_LIMIT: 48,
  LATE_SUBMISSION_DAYS: 3,
  HOURS_VARIANCE_THRESHOLD: 1.5, // 50% above average
};

export class AnomalyDetectionService {
  /**
   * Detect all anomalies in a timesheet
   */
  static detectAnomalies(
    timesheet: Timesheet,
    employee: Employee,
    historicalData?: EmployeeHistoricalData,
    allTimesheets?: Timesheet[]
  ): AnomalyDetectionResult {
    const anomalies: Anomaly[] = [];

    // High Priority Checks
    anomalies.push(...this.checkDailyHoursExceeded(timesheet));
    anomalies.push(...this.checkWeeklyHoursExceeded(timesheet));
    anomalies.push(...this.checkWeekendWorkNoApproval(timesheet));
    anomalies.push(...this.checkMissingFields(timesheet));

    // Medium Priority Checks
    if (historicalData) {
      anomalies.push(...this.checkHoursAboveAverage(timesheet, historicalData));
      anomalies.push(...this.checkUnusualProjectAllocation(timesheet, historicalData));
    }
    anomalies.push(...this.checkLateSubmission(timesheet));

    // Low Priority Checks
    if (allTimesheets) {
      anomalies.push(...this.checkFirstProjectEntry(timesheet, allTimesheets));
    }

    // Scheduled Booking Variance Checks
    console.log('🔍 [ANOMALY DETECTION] Checking variances for timesheet:', timesheet.id);
    const varianceAnomalies = this.checkScheduledBookingVariances(timesheet);
    console.log('🔍 [ANOMALY DETECTION] Found', varianceAnomalies.length, 'variance anomalies');
    anomalies.push(...varianceAnomalies);
    
    const dismissalAnomalies = this.checkDismissedBookings(timesheet);
    console.log('🔍 [ANOMALY DETECTION] Found', dismissalAnomalies.length, 'dismissal anomalies');
    anomalies.push(...dismissalAnomalies);

    // Calculate counts by severity
    const highPriorityCount = anomalies.filter(a => a.severity === 'high').length;
    const mediumPriorityCount = anomalies.filter(a => a.severity === 'medium').length;
    const lowPriorityCount = anomalies.filter(a => a.severity === 'low').length;

    return {
      timesheetId: timesheet.id,
      anomalies,
      highPriorityCount,
      mediumPriorityCount,
      lowPriorityCount,
      hasAnomalies: anomalies.length > 0,
    };
  }

  /**
   * Check for daily hours exceeding limit
   */
  private static checkDailyHoursExceeded(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const dailyTotals = this.calculateDailyTotals(timesheet);

    Object.entries(dailyTotals).forEach(([day, hours]) => {
      if (hours > THRESHOLDS.DAILY_HOURS_LIMIT) {
        anomalies.push({
          id: `${timesheet.id}-daily-${day}`,
          type: 'daily-hours-exceeded',
          severity: 'high',
          message: `${this.formatDay(day)}: ${hours.toFixed(1)}h exceeds daily limit`,
          details: `Daily hours limit is ${THRESHOLDS.DAILY_HOURS_LIMIT}h`,
          day,
          value: hours,
          threshold: THRESHOLDS.DAILY_HOURS_LIMIT,
        });
      }
    });

    return anomalies;
  }

  /**
   * Check for weekly hours exceeding limit
   */
  private static checkWeeklyHoursExceeded(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const weeklyTotal = this.calculateWeeklyTotal(timesheet);

    if (weeklyTotal > THRESHOLDS.WEEKLY_HOURS_LIMIT) {
      anomalies.push({
        id: `${timesheet.id}-weekly`,
        type: 'weekly-hours-exceeded',
        severity: 'high',
        message: `${weeklyTotal.toFixed(1)}h exceeds weekly limit of ${THRESHOLDS.WEEKLY_HOURS_LIMIT}h`,
        details: `Total weekly hours should not exceed ${THRESHOLDS.WEEKLY_HOURS_LIMIT}h`,
        value: weeklyTotal,
        threshold: THRESHOLDS.WEEKLY_HOURS_LIMIT,
      });
    }

    return anomalies;
  }

  /**
   * Check for weekend work without approval
   */
  private static checkWeekendWorkNoApproval(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    let saturdayHours = 0;
    let sundayHours = 0;

    timesheet.entries.forEach(entry => {
      saturdayHours += entry.hours.saturday;
      sundayHours += entry.hours.sunday;
    });

    if (saturdayHours > 0 || sundayHours > 0) {
      const days: string[] = [];
      if (saturdayHours > 0) days.push(`Saturday (${saturdayHours.toFixed(1)}h)`);
      if (sundayHours > 0) days.push(`Sunday (${sundayHours.toFixed(1)}h)`);

      anomalies.push({
        id: `${timesheet.id}-weekend`,
        type: 'weekend-work-no-approval',
        severity: 'high',
        message: `Weekend work detected: ${days.join(', ')}`,
        details: 'Weekend work requires pre-approval from manager',
        value: saturdayHours + sundayHours,
      });
    }

    return anomalies;
  }

  /**
   * Check for missing required fields
   */
  private static checkMissingFields(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const entriesWithMissingDescription = timesheet.entries.filter(
      entry => !entry.description || entry.description.trim().length === 0
    );

    if (entriesWithMissingDescription.length > 0) {
      anomalies.push({
        id: `${timesheet.id}-missing-desc`,
        type: 'missing-description',
        severity: 'high',
        message: `${entriesWithMissingDescription.length} ${entriesWithMissingDescription.length === 1 ? 'entry' : 'entries'} missing description`,
        details: 'All timesheet entries must include a description of work performed',
        value: entriesWithMissingDescription.length,
      });
    }

    return anomalies;
  }

  /**
   * Check for hours significantly above average
   */
  private static checkHoursAboveAverage(
    timesheet: Timesheet,
    historicalData: EmployeeHistoricalData
  ): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const weeklyTotal = this.calculateWeeklyTotal(timesheet);
    const threshold = historicalData.averageWeeklyHours * THRESHOLDS.HOURS_VARIANCE_THRESHOLD;

    if (weeklyTotal > threshold && historicalData.averageWeeklyHours > 0) {
      const percentageAbove = ((weeklyTotal - historicalData.averageWeeklyHours) / historicalData.averageWeeklyHours * 100).toFixed(0);
      
      anomalies.push({
        id: `${timesheet.id}-above-avg`,
        type: 'hours-above-average',
        severity: 'medium',
        message: `${weeklyTotal.toFixed(1)}h is ${percentageAbove}% above employee's average`,
        details: `Employee's average weekly hours: ${historicalData.averageWeeklyHours.toFixed(1)}h`,
        value: weeklyTotal,
        threshold: historicalData.averageWeeklyHours,
      });
    }

    return anomalies;
  }

  /**
   * Check for unusual project allocation patterns
   */
  private static checkUnusualProjectAllocation(
    timesheet: Timesheet,
    historicalData: EmployeeHistoricalData
  ): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const currentProjects = new Set(timesheet.entries.map(e => e.projectId));
    const unusualProjects = Array.from(currentProjects).filter(
      projectId => !historicalData.commonProjects.includes(projectId)
    );

    if (unusualProjects.length > 0 && historicalData.commonProjects.length > 0) {
      anomalies.push({
        id: `${timesheet.id}-unusual-project`,
        type: 'unusual-project-allocation',
        severity: 'medium',
        message: `${unusualProjects.length} ${unusualProjects.length === 1 ? 'project' : 'projects'} not in employee's usual allocation`,
        details: 'Employee is working on projects outside their normal assignment',
        value: unusualProjects.length,
      });
    }

    return anomalies;
  }

  /**
   * Check for late submission
   */
  private static checkLateSubmission(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    if (timesheet.submittedAt) {
      const weekEndingTime = timesheet.weekEnding.getTime();
      const submittedTime = timesheet.submittedAt.getTime();
      const daysDifference = Math.floor((submittedTime - weekEndingTime) / (1000 * 60 * 60 * 24));

      if (daysDifference > THRESHOLDS.LATE_SUBMISSION_DAYS) {
        anomalies.push({
          id: `${timesheet.id}-late`,
          type: 'late-submission',
          severity: 'medium',
          message: `Submitted ${daysDifference} days after week ending`,
          details: `Timesheets should be submitted within ${THRESHOLDS.LATE_SUBMISSION_DAYS} days`,
          value: daysDifference,
          threshold: THRESHOLDS.LATE_SUBMISSION_DAYS,
        });
      }
    }

    return anomalies;
  }

  /**
   * Check for first-time project entries
   */
  private static checkFirstProjectEntry(
    timesheet: Timesheet,
    allTimesheets: Timesheet[]
  ): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const currentProjects = new Set(timesheet.entries.map(e => e.projectId));
    
    // Get all previous timesheets for this employee
    const previousTimesheets = allTimesheets.filter(
      ts => ts.employeeId === timesheet.employeeId && 
           ts.id !== timesheet.id &&
           ts.weekEnding < timesheet.weekEnding
    );

    const previousProjects = new Set(
      previousTimesheets.flatMap(ts => ts.entries.map(e => e.projectId))
    );

    const newProjects = Array.from(currentProjects).filter(
      projectId => !previousProjects.has(projectId)
    );

    if (newProjects.length > 0) {
      anomalies.push({
        id: `${timesheet.id}-first-project`,
        type: 'first-project-entry',
        severity: 'low',
        message: `First timesheet entry on ${newProjects.length} ${newProjects.length === 1 ? 'project' : 'projects'}`,
        details: 'Employee is logging time to a new project for the first time',
        value: newProjects.length,
      });
    }

    return anomalies;
  }

  /**
   * Calculate historical data for an employee
   */
  static calculateHistoricalData(
    employeeId: string,
    allTimesheets: Timesheet[]
  ): EmployeeHistoricalData {
    const employeeTimesheets = allTimesheets.filter(
      ts => ts.employeeId === employeeId && ts.status === 'approved'
    );

    if (employeeTimesheets.length === 0) {
      return {
        employeeId,
        averageWeeklyHours: 0,
        averageDailyHours: 0,
        commonProjects: [],
        weekendWorkFrequency: 0,
      };
    }

    // Calculate average weekly hours
    const totalWeeklyHours = employeeTimesheets.reduce(
      (sum, ts) => sum + this.calculateWeeklyTotal(ts),
      0
    );
    const averageWeeklyHours = totalWeeklyHours / employeeTimesheets.length;

    // Calculate average daily hours (excluding weekends)
    let totalDailyHours = 0;
    let dayCount = 0;
    employeeTimesheets.forEach(ts => {
      const dailyTotals = this.calculateDailyTotals(ts);
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
        if (dailyTotals[day] > 0) {
          totalDailyHours += dailyTotals[day];
          dayCount++;
        }
      });
    });
    const averageDailyHours = dayCount > 0 ? totalDailyHours / dayCount : 0;

    // Find common projects (projects worked on in at least 30% of timesheets)
    const projectFrequency = new Map<string, number>();
    employeeTimesheets.forEach(ts => {
      const projects = new Set(ts.entries.map(e => e.projectId));
      projects.forEach(projectId => {
        projectFrequency.set(projectId, (projectFrequency.get(projectId) || 0) + 1);
      });
    });
    const threshold = employeeTimesheets.length * 0.3;
    const commonProjects = Array.from(projectFrequency.entries())
      .filter(([_, count]) => count >= threshold)
      .map(([projectId]) => projectId);

    // Calculate weekend work frequency
    const timesheetsWithWeekendWork = employeeTimesheets.filter(ts =>
      ts.entries.some(e => e.hours.saturday > 0 || e.hours.sunday > 0)
    ).length;
    const weekendWorkFrequency = timesheetsWithWeekendWork / employeeTimesheets.length;

    return {
      employeeId,
      averageWeeklyHours,
      averageDailyHours,
      commonProjects,
      weekendWorkFrequency,
    };
  }

  /**
   * Calculate daily totals for a timesheet
   */
  private static calculateDailyTotals(timesheet: Timesheet): Record<string, number> {
    const totals: Record<string, number> = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    timesheet.entries.forEach(entry => {
      Object.keys(totals).forEach(day => {
        totals[day] += entry.hours[day as keyof typeof entry.hours];
      });
    });

    return totals;
  }

  /**
   * Calculate weekly total for a timesheet
   */
  private static calculateWeeklyTotal(timesheet: Timesheet): number {
    return timesheet.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
  }

  /**
   * Format day name for display
   */
  private static formatDay(day: string): string {
    return day.charAt(0).toUpperCase() + day.slice(1);
  }

  /**
   * Get severity icon
   */
  static getSeverityIcon(severity: AnomalySeverity): string {
    switch (severity) {
      case 'high':
        return '🔴';
      case 'medium':
        return '⚠️';
      case 'low':
        return 'ℹ️';
    }
  }

  /**
   * Get severity color class
   */
  static getSeverityColorClass(severity: AnomalySeverity): string {
    switch (severity) {
      case 'high':
        return 'text-red-600 dark:text-red-400';
      case 'medium':
        return 'text-amber-600 dark:text-amber-400';
      case 'low':
        return 'text-blue-600 dark:text-blue-400';
    }
  }

  /**
   * Get severity background class
   */
  static getSeverityBgClass(severity: AnomalySeverity): string {
    switch (severity) {
      case 'high':
        return 'bg-red-100 dark:bg-red-950/30 border-red-300 dark:border-red-800';
      case 'medium':
        return 'bg-amber-100 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-950/30 border-blue-300 dark:border-blue-800';
    }
  }

  /**
   * Check for scheduled booking variances
   */
  private static checkScheduledBookingVariances(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    const scheduledEntries = timesheet.entries.filter(e => e.isFromSchedule && !e.isDismissed);
    if (scheduledEntries.length > 0) {
      console.log(`🔍 [Variance Check] Timesheet ${timesheet.id}:`, {
        totalEntries: timesheet.entries.length,
        scheduledEntries: scheduledEntries.length,
        entries: scheduledEntries.map(e => ({
          id: e.id,
          project: e.projectId,
          scheduledHours: e.scheduledHours,
          actualHours: e.hours
        }))
      });
    }
    
    timesheet.entries.forEach(entry => {
      // Skip dismissed bookings (handled separately)
      if (entry.isDismissed) return;
      
      const variance = calculateEntryVariance(entry);
      
      if (!variance) {
        if (entry.isFromSchedule) {
          console.log(`⚠️ [Variance] Entry ${entry.id}: No variance calculated (missing data?)`, {
            isFromSchedule: entry.isFromSchedule,
            hasScheduledHours: !!entry.scheduledHours,
            scheduledHours: entry.scheduledHours,
            actualHours: entry.hours
          });
        }
        return;
      }
      
      console.log(`📊 [Variance] Entry ${entry.id}:`, variance);
      
      // Only flag significant variances (>10%)
      if (Math.abs(variance.percentage) > 10) {
        const project = entry.projectId; // Project name would need to be resolved
        const varianceText = formatVariance(variance.variance, variance.percentage);
        
        anomalies.push({
          id: `${timesheet.id}-variance-${entry.id}`,
          type: 'scheduled-booking-variance',
          severity: variance.severity,
          message: `Scheduled booking variance: ${varianceText}`,
          details: `Scheduled: ${variance.scheduled}h, Logged: ${variance.actual}h. ${
            variance.variance > 0 
              ? 'Employee logged more hours than scheduled.' 
              : 'Employee logged fewer hours than scheduled.'
          }`,
          value: variance.actual,
          threshold: variance.scheduled,
        });
      }
    });

    return anomalies;
  }

  /**
   * Check for dismissed scheduled bookings
   */
  private static checkDismissedBookings(timesheet: Timesheet): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    const dismissedBookings = timesheet.entries.filter(e => e.isDismissed);
    
    if (dismissedBookings.length > 0) {
      console.log(`🚫 [Dismissed] Timesheet ${timesheet.id}:`, {
        dismissedCount: dismissedBookings.length,
        entries: dismissedBookings.map(e => ({
          id: e.id,
          project: e.projectId,
          reason: e.dismissalReason,
          scheduledHours: e.scheduledHours
        }))
      });
    }
    
    dismissedBookings.forEach(entry => {
      const scheduledHours = entry.scheduledHours 
        ? Object.values(entry.scheduledHours).reduce((sum, h) => sum + h, 0)
        : 0;
      
      anomalies.push({
        id: `${timesheet.id}-dismissed-${entry.id}`,
        type: 'scheduled-booking-dismissed',
        severity: 'medium',
        message: `Scheduled booking dismissed (${scheduledHours}h scheduled)`,
        details: entry.dismissalReason 
          ? `Reason: "${entry.dismissalReason}"` 
          : 'No reason provided for dismissal',
        value: scheduledHours,
      });
    });

    return anomalies;
  }
}
